import Fuse from "fuse.js";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { SearchBox, useSearch } from "@transfr-inc/dashboard-components/forms";

import "./trainee.classroom.prog.searchbox.scss";

const searchKeysMap = {
  title: "activity",
  unitTitle: "unit",
  lessonTitle: "lesson",
};

export const ActivitiesSearchbox = ({
  data,
  defaultTerm,
  onSearchTermChange,
}) => {
  const fuse = useMemo(() => {
    const fuseOptions = {
      includeMatches: true,
      threshold: 0.3,
      useExtendedSearch: true,
      keys: [{ name: "title", weight: 2 }, "unitTitle", "lessonTitle"],
    };

    const fuseIndex = Fuse.createIndex(fuseOptions.keys, data);
    return new Fuse(data, fuseOptions, fuseIndex);
  }, [data]);

  const search = useSearch({
    data,
    queryFn: (_, term) => {
      const { term: searchTerm, limit = 5 } =
        typeof term === "string" ? { term } : term;

      if (!searchTerm) {
        return undefined;
      }

      const query = {
        $or: [
          { title: searchTerm },
          { unitTitle: searchTerm },
          { lessonTitle: searchTerm },
        ],
      };

      return fuse.search(query, { limit });
    },
  });

  const onResultSelectedHandler = ({ item }) => {
    onSearchTermChange({ resourceIds: [item.id], term: item.title });
  };

  const onSearch = (term) => {
    const results = search.queryData({ term, limit: undefined });

    onSearchTermChange({
      resourceIds: results?.map((r) => r.item.id),
      term,
    });
  };

  const onSearchTermChangeHandler = (term) => {
    if (!term) {
      onSearch(term);
    }
  };

  return (
    <SearchBox
      className="trainee-classroom-searchbox"
      customOptionLabel={(r) => r.item.title}
      optionComponent={(r) => (
        <div className="trainee-classroom-searchbox-option">
          <div className="trainee-classroom-searchbox-option-name">
            {r.item.title}
          </div>
          <div className="trainee-classroom-searchbox-option-matches">
            {r.matches.map((m) => (
              <span key={m.key}>
                <strong>{searchKeysMap[m.key]}:</strong> {m.value}
              </span>
            ))}
          </div>
        </div>
      )}
      data={data}
      onResultSelected={onResultSelectedHandler}
      query={search.queryData}
      placeholder="Search by unit, lesson, activity..."
      onSearch={onSearch}
      onSearchTermChange={onSearchTermChangeHandler}
      searchButton
      searchTerm={defaultTerm}
    />
  );
};

ActivitiesSearchbox.propTypes = {
  data: PropTypes.array.isRequired,
  defaultTerm: PropTypes.string,
  onSearchTermChange: PropTypes.func.isRequired,
};
